"use client";
import React, { FC, useState } from "react";
import axios from "axios";
import clsx from "clsx";
import { trimEnd } from "lodash";
import { useForm } from "react-hook-form";
import { useSearchParams } from "next/navigation";

import { env } from "@/env.mjs";
import TButton from "@/components/atoms/buttons/TButton";
import TCheckbox from "@/components/atoms/checkbox/TCheckbox";
import TInput from "@/components/atoms/input/TInput";
import TSelect from "@/components/atoms/select/TSelect";
import TTextArea from "@/components/atoms/text-area/TTextArea";
import { yupResolver } from "@hookform/resolvers/yup";
import { ISecurityRequestAccessFormRequest } from "@/api/dtos/security-request-access.type";
import { securityRequestAccessSchema } from "@/app/api/validationSchemas";
import { tNotifications } from "@/components/atoms/notification/TNotification";
import TPhoneInput from "@/components/atoms/phone-input/TPhoneInput";

import GoogleReCaptcha from "../common/GoogleReCaptcha";
import useGoogleReCaptcha from "@/hooks/useGoogleReCaptcha";
import { securityRequestAccessRequestsList } from "@/constants/security-request-access-form";
import { handleException, IAxiosError } from "@/utils/exception.helper";
import ConsentMessage from "@/components/common/ConsentMessage";

// -------------------------------------------------------------

type ISecurityRequestAccessFormSectionProps = {};

type IDefaultValues = {
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  phone?: string;
  requestFor: string | null;
  specificRequestFor?: string;
  reason: string;
  emailConcent?: boolean;
};

const defaultValues: IDefaultValues = {
  firstName: "",
  lastName: "",
  email: "",
  companyName: "",
  phone: "",
  requestFor: "",
  specificRequestFor: "",
  reason: "",
  emailConcent: true,
};

// Format the request param from like ( , _, -, +) separated to space separated
const decodeAndFormatParam = (param: string | null) => {
  if (!param) return undefined;

  // Decode URL-encoded characters like %20, %2B, %2D, %5F
  let decodedParam = decodeURIComponent(param);

  // Validate: Only allow letters, numbers, spaces, `_`, `-`, and `+`
  if (!/^[a-zA-Z0-9 _+\-]+$/.test(decodedParam)) {
    return undefined; // Return undefined for invalid inputs
  }

  return decodedParam
    .replace(/[_+\-]+/g, " ") // Replace `_`, `+`, and `-` with spaces
    .replace(/\s+/g, " ") // Remove extra spaces
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize each word
};

const SecurityRequestAccessFormSection: FC<
  ISecurityRequestAccessFormSectionProps
> = () => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const {
    reCaptchaRef,
    isReCaptchaAvailable,
    isRecaptchaVarified,
    resetReCaptcha,
  } = useGoogleReCaptcha();

  const searchParams = useSearchParams();

  const request = decodeAndFormatParam(searchParams.get("request"));

  const onSubmit = async (formData: IDefaultValues) => {
    const { success, token } = await isRecaptchaVarified();
    if (!success) return;

    try {
      setIsSubmitting(true);

      await axios.post<ISecurityRequestAccessFormRequest>(
        `${trimEnd(env.NEXT_PUBLIC_SITE_URL, "/")}/api/request-access`,
        formData,
        {
          headers: token ? { "x-recaptcha-token": token } : undefined,
        },
      );
      reset();
      tNotifications.success({
        title: "Success",
        message: "Form submitted successfully!",
      });
    } catch (error) {
      handleException(error as IAxiosError);
    } finally {
      setIsSubmitting(false);
      resetReCaptcha();
    }
  };

  const { handleSubmit, reset, control, setValue, watch } = useForm({
    defaultValues: {
      ...defaultValues,
      requestFor: request ?? "Other",
    },
    resolver: yupResolver(securityRequestAccessSchema),
  });

  return (
    <section className="container py-5 lg:py-8 xl:py-[50px]">
      <div className="w-full">
        <h1 className="mb-7 text-h4 font-medium text-black-900">
          Certbar Security Request Access
        </h1>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="text-re flex w-full flex-col justify-center gap-4"
          id="request-access-form"
        >
          <div className="flex flex-col justify-center gap-4 md:flex-row">
            <TInput
              name="firstName"
              control={control}
              placeholder="First Name *"
            />
            <TInput
              name="lastName"
              control={control}
              placeholder="Last Name *"
            />
          </div>
          <div className="flex flex-col justify-center gap-4 md:flex-row">
            <TInput name="email" control={control} placeholder="Work Email *" />
            <TInput
              name="companyName"
              control={control}
              placeholder="Company Name *"
            />
          </div>
          <div className="flex flex-col justify-center gap-4 md:flex-row">
            <TPhoneInput control={control} name="phone" />
            <div className="flex w-full flex-col gap-4">
              <TInput
                name="requestFor"
                control={control}
                placeholder="Request for ? *"
                disabled
              />
              {(watch("requestFor") === "Other" ||
                watch("requestFor") === "Others") && (
                <TInput
                  name="specificRequestFor"
                  control={control}
                  placeholder="Please specify what are you requesting for ? *"
                />
              )}
            </div>
          </div>
          <TTextArea name="reason" control={control} placeholder="Reason *" />
          <TCheckbox
            label={<ConsentMessage />}
            checked={watch("emailConcent")}
            onChange={(e) => setValue("emailConcent", e.currentTarget.checked)}
          />

          <div>
            {/* Google reCAPTCHA component */}
            {isReCaptchaAvailable && <GoogleReCaptcha ref={reCaptchaRef} />}

            <TButton
              id="request-access-form-submit-button"
              type="submit"
              variant="primary"
              className={clsx("h-fit", isSubmitting && "!cursor-not-allowed")}
              disabled={isSubmitting}
            >
              Subscribe
            </TButton>
          </div>
        </form>
      </div>
    </section>
  );
};

export default SecurityRequestAccessFormSection;
